import { useEffect, useMemo, useState } from "react";
import { Trash2, Download, Search, Mail, Phone } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { api, apiError } from "../../lib/api";

const STATUSES = { nuova: "Nuova", in_lavorazione: "In lavorazione", contattata: "Contattata", chiusa: "Chiusa" };
const EMAIL_BADGE = { sent: "text-emerald-300", failed: "text-red-300", pending: "text-slate-400" };

const toCsv = (rows) => {
  const cols = ["created_at", "full_name", "company", "phone", "email", "sector", "area", "status"];
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  return [cols.join(";"), ...rows.map((r) => cols.map((c) => esc(r[c])).join(";"))].join("\n");
};

export const LeadsTab = ({ onChange }) => {
  const [leads, setLeads] = useState([]);
  const [q, setQ] = useState("");
  const load = () => api.get("/admin/leads").then((r) => setLeads(Array.isArray(r.data) ? r.data : [])).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => leads.filter((l) => JSON.stringify(l).toLowerCase().includes(q.toLowerCase())), [leads, q]);

  const setStatus = async (id, status) => {
    await api.patch(`/admin/leads/${id}`, { status });
    setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
    onChange?.();
  };
  const remove = async (id) => {
    if (!window.confirm("Eliminare questa richiesta?")) return;
    await api.delete(`/admin/leads/${id}`);
    setLeads((ls) => ls.filter((l) => l.id !== id));
    onChange?.();
  };
  const exportCsv = () => {
    const blob = new Blob([toCsv(filtered)], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "richieste-termonline.csv";
    a.click();
  };

  return (
    <div data-testid="leads-tab">
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input data-testid="leads-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per nome, azienda, zona..."
            className="w-full h-10 rounded-lg bg-white/[0.04] border hairline pl-9 pr-3 text-sm text-white outline-none" />
        </div>
        <button data-testid="leads-export" onClick={exportCsv} className="h-10 px-4 rounded-lg border hairline text-sm text-white inline-flex items-center gap-2 hover:bg-white/5"><Download className="w-4 h-4" />Esporta CSV</button>
      </div>
      {filtered.length === 0 && <p data-testid="leads-empty" className="text-slate-400 text-sm py-10 text-center">Nessuna richiesta ricevuta.</p>}
      <div className="space-y-3">
        {filtered.map((l) => (
          <div key={l.id} data-testid={`lead-row-${l.id}`} className="rounded-2xl border hairline bg-white/[0.02] p-4 grid md:grid-cols-[1.4fr,1fr,1fr,auto] gap-4 items-center">
            <div>
              <p className="text-white font-semibold">{l.full_name} <span className="text-slate-400 font-normal">· {l.company}</span></p>
              <p className="text-xs text-slate-400 mt-1">{new Date(l.created_at).toLocaleString("it-IT")} · email notifica: <span className={EMAIL_BADGE[l.email_status]}>{l.email_status}</span></p>
            </div>
            <div className="text-sm space-y-1">
              <a href={`tel:${l.phone}`} className="flex items-center gap-2 text-slate-200 hover:text-white"><Phone className="w-3.5 h-3.5" />{l.phone}</a>
              <a href={`mailto:${l.email}`} className="flex items-center gap-2 text-slate-200 hover:text-white break-all"><Mail className="w-3.5 h-3.5" />{l.email}</a>
            </div>
            <div className="text-sm"><p className="text-[#7fd6fb]">{l.sector}</p><p className="text-slate-300">{l.area}</p></div>
            <div className="flex items-center gap-2">
              <Select value={l.status} onValueChange={(v) => setStatus(l.id, v)}>
                <SelectTrigger data-testid={`lead-status-${l.id}`} className="w-40 h-9 bg-white/[0.04] border hairline text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0e1528] border hairline text-white">
                  {Object.entries(STATUSES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
              <button data-testid={`lead-delete-${l.id}`} onClick={() => remove(l.id)} className="w-9 h-9 grid place-items-center rounded-lg text-slate-400 hover:text-red-400" aria-label="Elimina"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
